import { PolicyGetResponse, PolicyReinstatementReason, PolicyTransactionType } from '@lio/api-types';

import {
  PolicyTransactionTypes,
  PolicyTransactionStatus,
  PolicyCancelationMethod,
  PolicyCancelationReason,
  PolicyCancelationSource,
  PolicyTransaction,
} from '@models/policyTransaction';
import { setDateToMidnight } from '@utils/date';
import { companyOptions } from '../PolicyTransactionDialog.constants';

export const getUpdatePolicyTransactionRequestFromFormData = ({
  transactionType,
  policyTransaction,
  policy,
  formData,
}: {
  transactionType: PolicyTransactionType | 'Rollback';
  policyTransaction: PolicyTransaction;
  policy: PolicyGetResponse;
  formData: { [fieldName: string]: any };
}) => {
  switch (transactionType) {
    case PolicyTransactionTypes.Endorsement:
      return {
        body: {
          policyEndorsementTransaction: { reason: formData.reason },
          description: formData.description,
        },
        policyTransactionId: policyTransaction?.id ?? '',
      };

    case PolicyTransactionTypes.Cancelation: {
      const reason = formData.reason as PolicyCancelationReason;
      const includesCompanyReasons = companyOptions.map((cr) => cr.value).includes(reason);
      let source: PolicyCancelationSource | string;

      if (reason === PolicyCancelationReason.Other) {
        source = formData.source as PolicyCancelationSource;
      } else if (includesCompanyReasons) {
        source = PolicyCancelationSource.Company;
      } else {
        source = PolicyCancelationSource.Insured;
      }

      return {
        body: {
          status: PolicyTransactionStatus.Processed,
          policyCancelationTransaction: {
            source: source as PolicyCancelationSource,
            reason,
            method: formData.method as PolicyCancelationMethod,
          },
          description: formData.notes,
          effectiveOn: setDateToMidnight(new Date(policyTransaction.effectiveOn)),
        },
        policyTransactionId: policyTransaction?.id ?? '',
      };
    }
    case PolicyTransactionTypes.Reinstatement: {
      return {
        body: {
          status: PolicyTransactionStatus.Processed,
          policyReinstatementTransaction: {
            claimsLossesPostCancelation: formData.claimsLossesPostCancelation === 'true',
            reason: formData.reason as PolicyReinstatementReason,
            certifiedMailRequired: formData.certifiedMailRequired === 'true',
          },
          description: formData.description,
          effectiveOn: setDateToMidnight(new Date(policyTransaction.effectiveOn)),
        },
        policyTransactionId: policyTransaction?.id ?? '',
      };
    }

    case 'Rollback': {
      const isCustomerBlocked = Boolean((policy as PolicyGetResponse)?.customer?.isBlocked);
      return {
        body: {
          status: PolicyTransactionStatus.RolledBack,
          ...(isCustomerBlocked && { customer: { isBlocked: false } }),
        },
        policyTransactionId: policyTransaction?.id ?? '',
      };
    }

    default:
      throw Error('Transaction Type Update Not Implemented');
  }
};
